import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence, useAnimationControls } from "framer-motion";
import { TennisBall, Trophy, ArrowsOut, ArrowsIn } from "@phosphor-icons/react";
import confetti from "canvas-confetti";
import api, { API } from "../lib/api";
import { useRealtime } from "../hooks/useRealtime";
import CameraFeed from "../components/CameraFeed";
import WinnerCelebration from "../components/WinnerCelebration";

function teamName(t) { return t.p2 ? `${t.p1} / ${t.p2}` : t.p1; }

function TeamRow({ team, sets, games, points, accent, winner, serving, servingPlayer }) {
  const color = accent === "green" ? "#CCFF00" : "#00D1FF";
  const effPlayer = !winner && serving ? (servingPlayer === 2 && !team.p2 ? 1 : servingPlayer) : null;
  return (
    <div className="flex items-center justify-between gap-4 w-full">
      <div className="flex items-center gap-3 min-w-0 flex-1" style={{ color: winner ? color : "#fff" }}>
        {winner && <Trophy size={32} weight="fill" style={{ color }} className="shrink-0" />}
        <div className="flex flex-col min-w-0 font-heading font-extrabold uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">
          <span className="truncate flex items-center gap-2">
            {effPlayer === 1 && <TennisBall size={26} weight="fill" className="text-padel-green shrink-0 animate-pulse-glow" />}
            {team.p1}
          </span>
          {team.p2 && (
            <span className="truncate flex items-center gap-2">
              {effPlayer === 2 && <TennisBall size={26} weight="fill" className="text-padel-green shrink-0 animate-pulse-glow" />}
              {team.p2}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 sm:gap-7 shrink-0">
        <span className="font-heading text-4xl sm:text-5xl tabular-nums text-muted-foreground w-9 text-center leading-none">{sets}</span>
        <span className="font-heading text-4xl sm:text-5xl tabular-nums text-white w-9 text-center leading-none">{games}</span>
        <AnimatePresence mode="popLayout">
          <motion.span
            key={points}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: [1.4, 1], opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="font-heading text-7xl sm:text-8xl lg:text-9xl font-extrabold tabular-nums w-28 sm:w-36 lg:w-44 text-center leading-none"
            style={{ color, textShadow: `0 0 28px ${color}cc` }}
          >
            {points}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}

function CourtCard({ court, hero }) {
  const m = court.match;
  const live = m && m.status === "live";
  const showCam = court.camera_type !== "none" && court.camera_enabled !== false && court.camera_url;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden border bg-[#0A0A0C] flex flex-col lg:flex-row ${live ? "border-padel-green/60 glow-green" : "border-white/10"}`}
    >
      {/* LEFT: camera + court name (only when camera enabled) */}
      {showCam && (
        <div className="relative lg:w-[38%] shrink-0">
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-3 z-10 bg-gradient-to-b from-black/80 to-transparent">
            <span className="font-heading text-2xl sm:text-3xl font-extrabold uppercase tracking-wide">{court.name}</span>
            {live ? (
              <span className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-padel-green">
                <span className="w-2 h-2 rounded-full bg-padel-green animate-pulse-glow" /> En vivo
              </span>
            ) : (
              <span className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground">{m ? (m.status === "finished" ? "Finalizado" : "Programado") : "Libre"}</span>
            )}
          </div>
          <div className="bg-black aspect-video lg:aspect-auto lg:h-full lg:min-h-[220px]">
            <CameraFeed type={court.camera_type} url={court.camera_url} />
          </div>
        </div>
      )}

      {/* RIGHT: scoreboard (big) */}
      <div className={`flex-1 flex flex-col justify-center px-6 sm:px-10 py-6 gap-5 ${showCam ? "border-t lg:border-t-0 lg:border-l border-white/10" : ""}`}>
        {!showCam && (
          <div className="flex items-center justify-between mb-1">
            <span className="font-heading text-2xl sm:text-3xl font-extrabold uppercase tracking-wide">{court.name}</span>
            {live ? (
              <span className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-padel-green">
                <span className="w-2 h-2 rounded-full bg-padel-green animate-pulse-glow" /> En vivo
              </span>
            ) : (
              <span className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground">{m ? (m.status === "finished" ? "Finalizado" : "Programado") : "Libre"}</span>
            )}
          </div>
        )}
        {m ? (
          <>
            <div className="flex justify-end gap-4 sm:gap-7 text-[11px] tracking-[0.25em] uppercase text-muted-foreground font-bold">
              <span className="w-9 text-center">Set</span>
              <span className="w-9 text-center">Gm</span>
              <span className="w-28 sm:w-36 lg:w-44 text-center">{m.score.in_tiebreak ? "TB" : "Pts"}</span>
            </div>
            <TeamRow team={m.team1} sets={m.score.sets1} games={m.score.games1} points={m.score.points1} accent="blue" winner={m.score.winner === 1} serving={live && m.score.serving === 1} servingPlayer={m.score.serving_player} />
            <div className="h-px bg-white/10" />
            <TeamRow team={m.team2} sets={m.score.sets2} games={m.score.games2} points={m.score.points2} accent="green" winner={m.score.winner === 2} serving={live && m.score.serving === 2} servingPlayer={m.score.serving_player} />
          </>
        ) : (
          <p className="text-center text-muted-foreground py-10 font-heading text-2xl uppercase tracking-widest">Sin partido</p>
        )}
      </div>

      {m && m.score.winner && (
        <WinnerCelebration
          team={m.score.winner === 1 ? m.team1 : m.team2}
          accent={m.score.winner === 1 ? "blue" : "green"}
          size="md"
        />
      )}
    </motion.div>
  );
}

// Auto-scrolls children vertically when they don't fit the viewport. `signature`
// changes only when layout-affecting data changes (courts/cameras/match presence),
// so live score updates don't restart the scroll.
function VerticalAutoScroll({ signature, children }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const controls = useAnimationControls();

  useEffect(() => {
    let cancelled = false;
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    const run = async () => {
      await wait(700);
      if (cancelled) return;
      const outer = outerRef.current, inner = innerRef.current;
      if (!outer || !inner) return;
      const overflow = inner.scrollHeight - outer.clientHeight;
      controls.set({ y: 0 });
      if (overflow <= 12) return;
      const dur = Math.max(6, overflow / 45);
      while (!cancelled) {
        await wait(4500); if (cancelled) break;
        await controls.start({ y: -overflow, transition: { duration: dur, ease: "linear" } }); if (cancelled) break;
        await wait(4500); if (cancelled) break;
        await controls.start({ y: 0, transition: { duration: dur, ease: "linear" } }); if (cancelled) break;
      }
    };
    run();
    return () => { cancelled = true; };
  }, [signature, controls]);

  return (
    <div ref={outerRef} className="flex-1 min-h-0 overflow-hidden">
      <motion.div ref={innerRef} animate={controls} className="flex flex-col gap-5">
        {children}
      </motion.div>
    </div>
  );
}

export default function Display() {
  const [data, setData] = useState(null);
  const [clock, setClock] = useState("");
  const [isFs, setIsFs] = useState(false);
  const winners = useRef({});

  const load = useCallback(() => api.get("/display").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen().catch(() => {});
  };

  useEffect(() => {
    const t = setInterval(() => setClock(new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })), 1000);
    return () => clearInterval(t);
  }, []);

  useRealtime(useCallback((msg) => {
    if (msg.type === "refresh" || msg.type === "branding") return load();
    if (msg.type === "match_update" && msg.match) {
      setData((prev) => {
        if (!prev) return prev;
        const courts = prev.courts.map((c) =>
          c.id === msg.match.court_id ? { ...c, match: ["live", "scheduled"].includes(msg.match.status) || msg.match.score.winner ? msg.match : c.match } : c
        );
        return { ...prev, courts };
      });
      const w = msg.match.score.winner;
      if (w && winners.current[msg.match.id] !== w) {
        winners.current[msg.match.id] = w;
        confetti({ particleCount: 200, spread: 120, origin: { y: 0.4 }, colors: ["#CCFF00", "#00D1FF", "#fff"] });
      }
    }
  }, [load]));

  if (!data) return <div className="min-h-screen flex items-center justify-center text-padel-blue font-heading uppercase animate-pulse-glow">Cargando pantalla…</div>;

  const heroIdx = data.courts.findIndex((c) => c.match && c.match.status === "live");
  const layoutSig = data.courts
    .map((c) => `${c.id}:${c.camera_type !== "none" && c.camera_enabled !== false && c.camera_url ? "cam" : "nocam"}:${c.match ? c.match.status : "free"}`)
    .join("|");

  return (
    <div className="h-screen overflow-hidden grain relative flex flex-col">
      <div className="absolute inset-0 z-0">
        <img src="https://images.pexels.com/photos/35525977/pexels-photo-35525977.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-[#0A0A0C]/85" />
      </div>

      <div className="relative z-10 max-w-[1700px] w-full mx-auto px-6 py-6 flex flex-col flex-1 min-h-0">
        {/* header */}
        <header className="flex items-center justify-between mb-6 shrink-0">
          <div className="flex items-center gap-4">
            {data.branding.has_logo ? (
              <img data-testid="display-logo" src={`${API}/branding/logo`} alt="logo" className="h-12 sm:h-16 object-contain" />
            ) : (
              <TennisBall size={44} weight="fill" className="text-padel-green" />
            )}
            <h1 data-testid="display-company-name" className="font-heading text-3xl sm:text-5xl font-extrabold uppercase tracking-tight">
              {data.branding.company_name}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-mono text-xl sm:text-3xl text-padel-blue">{clock}</p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Resultados en vivo</p>
            </div>
            <button
              data-testid="display-fullscreen-btn"
              onClick={toggleFullscreen}
              title={isFs ? "Salir de pantalla completa" : "Pantalla completa"}
              className="p-2.5 border border-white/15 text-muted-foreground hover:text-padel-green hover:border-padel-green/50 transition-colors"
            >
              {isFs ? <ArrowsIn size={24} weight="bold" /> : <ArrowsOut size={24} weight="bold" />}
            </button>
          </div>
        </header>

        {data.courts.length === 0 ? (
          <p className="text-center text-muted-foreground py-20 font-heading text-2xl uppercase tracking-widest">No hay canchas configuradas</p>
        ) : (
          <VerticalAutoScroll signature={layoutSig}>
            {data.courts.map((c, i) => (
              <CourtCard key={c.id} court={c} hero={i === heroIdx} />
            ))}
          </VerticalAutoScroll>
        )}
      </div>
    </div>
  );
}
